// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { formatUsDate, formatUsTimestamp } from '../../api/format'
import { pharmacyAppOrigin } from '../../auth/pharmacyUrl'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  ChevronLeft, AlertCircle, Loader2, Building2, ShieldCheck, KeyRound,
  Copy, RotateCw, Pause, Play, Mail, CheckCircle2, Pencil, Save, X,
  FileText, Upload, Download, Archive, Plus, UserPlus, Key,
} from 'lucide-react'
import * as api from '../../api/client'
import { useAutoRefresh } from '../../components/useAutoRefresh'
import { friendlyError } from '../../api/errors'

/**
 * /admin/pharmacies/{id} — pharmacy detail + onboarding actions.
 *
 * Shows the pharmacy's metadata + BAA + COO approval (read-only post-create).
 * Then surfaces the next-action card based on onboarding_state:
 *
 *   pending_admin         → "Invite primary admin" form
 *   pending_ingest_setup  → "Generate ingest credentials" button
 *   active                → "Active credentials in use" + rotate button
 *   suspended             → "Reactivate" button
 *
 * Generated credentials are shown ONCE in a copyable card. After dismissing,
 * the password is gone — to recover, rotate a new one.
 */
import PharmacySsoConfig from './PharmacySsoConfig'
import PharmacySenderDomainCard from '../../components/admin/PharmacySenderDomainCard'
import PharmacyMailServerCard from '../../components/admin/PharmacyMailServerCard'
import PharmacyEmailTemplatesCard from '../../components/admin/PharmacyEmailTemplatesCard'
import PharmacyPodStorageCard from '../../components/admin/PharmacyPodStorageCard'
import PharmacyCareRxBackfillCard from '../../components/admin/PharmacyCareRxBackfillCard'
import CarrierAccountsPanel from '../../components/shipping/CarrierAccountsPanel'
import PharmacyModulesCard from '../../components/admin/PharmacyModulesCard'
import CareRxFeaturesPanel from '../../components/admin/CareRxFeaturesPanel'
import AppTabs from '../../components/ui/AppTabs'

const TABS = [{ value: 'profile', label: 'Profile' }, { value: 'shipping', label: 'Shipping' }, { value: 'carerx', label: 'CareRx' }]
/** ?tab= picks a section; anything unknown (or none) is the Profile tab. */
export const tabFromParam = (v: string | null) => TABS.find(t => t.value === v)?.value ?? 'profile'

export default function PharmacyDetailPage() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const tab = tabFromParam(params.get('tab'))
  const [detail, setDetail] = useState<api.PharmacyDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // One-time secret display (generated or rotated credentials)
  const [revealed, setRevealed] = useState<api.CredentialsResponse | null>(null)
  const [revealedInvite, setRevealedInvite] = useState<{ link: string; email: string; expires_at: string } | null>(null)

  async function reload() {
    setLoading(true); setError(null)
    try { setDetail(await api.getPharmacy(id)) }
    catch (e) { setError(friendlyError(e)) }
    finally { setLoading(false) }
  }
  useEffect(() => { reload() }, [id])

  if (loading && !detail) return <div style={loadingBox}><Loader2 size={16} className="spin" /> Loading…</div>
  if (error) return <div style={errorBox}><AlertCircle size={14} /> {error}</div>
  if (!detail) return null

  const p = detail.pharmacy

  return (
    <div style={{ maxWidth: '100%' }}>
      <button onClick={() => navigate('/admin/pharmacies')} style={backLinkStyle}>
        <ChevronLeft size={14} /> Back to pharmacies
      </button>

      <div style={titleRowStyle}>
        {p.logo_url && (
          <img src={p.logo_url} alt="" style={{
            width: 40, height: 40, borderRadius: 6, objectFit: 'contain',
            border: '1px solid var(--color-border)', background: '#fff', padding: 2,
          }} />
        )}
        <h2 style={h2Style}>
          {!p.logo_url && <Building2 size={18} aria-hidden="true" style={{ marginRight: 6, verticalAlign: '-3px' }} />}
          {p.display_name_override || p.name}
        </h2>
        <StateBadge state={p.onboarding_state} />
      </div>
      {p.parent_organization_name && (
        <div style={{ marginTop: -8, marginBottom: 16, fontSize: 13, color: 'var(--color-text-soft)' }}>
          Branch of <strong>{p.parent_organization_name}</strong>
          {p.branch_label && <> · {p.branch_label}</>}
        </div>
      )}

      <AppTabs ariaLabel="Pharmacy sections" value={tab} tabs={TABS}
        onChange={v => setParams(v === 'profile' ? {} : { tab: v }, { replace: true })} />
      <div style={{ marginTop: 16 }}>
      {tab === 'shipping' ? <CarrierAccountsPanel pharmacyId={p.id} /> : tab === 'carerx' ? <>
        {/* D-CRX-87: the CareRx module and its three sub-switches moved here from Profile (same endpoints). */}
        <PharmacyModulesCard pharmacyId={p.id} family="carerx" title="CareRx module"
          subtitle="Turn CareRx on for this pharmacy (or start a 90-day trial), and choose which of its parts apply." />
        <CareRxFeaturesPanel pharmacyId={p.id} />
      </> : <>
      {/* One-time credentials reveal */}
      {revealed && (
        <CredentialsRevealCard creds={revealed} onDismiss={() => setRevealed(null)} />
      )}
      {revealedInvite && (
        <InviteRevealCard invite={revealedInvite} onDismiss={() => setRevealedInvite(null)} />
      )}

      {/* Next action */}
      <NextActionCard
        detail={detail}
        onInvited={(r) => { setRevealedInvite(r); reload() }}
        onCredsGenerated={(r) => { setRevealed(r); reload() }}
        onReload={reload}
      />

      {/* Pharmacy metadata — editable (WI-821) */}
      <PharmacySection pharmacy={p} onSaved={reload} />

      {/* Sibling branches under same parent org (WI-820) */}
      {detail.sibling_branches && detail.sibling_branches.length > 0 && (
        <Section title={`Other branches in ${p.parent_organization_name}`}>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {detail.sibling_branches.map(b => (
              <li key={b.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--color-border)' }}>
                <button
                  onClick={() => navigate(`/admin/pharmacies/${b.id}`)}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, fontSize: 13, color: 'var(--color-primary, #0d9488)', fontFamily: 'inherit' }}
                >
                  {b.branch_label ? `${b.branch_label} — ` : ''}{b.name}
                </button>
                <StateBadge state={b.onboarding_state as api.PharmacyListItem['onboarding_state']} />
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* BAA — reference + effective window, editable by SaaS admin (audited; docs stay in WI-822 flow) */}
      <BaaSection pharmacy={p} onSaved={reload} />

      {/* COO approval */}
      <Section title="Engagement approval" tone="compliance">
        <Grid>
          <Field label="Approved by">{p.coo_approver_name}</Field>
          <Field label="Approver email">{p.coo_approval_recorded_by}</Field>
          <Field label="Recorded at">
            {formatUsTimestamp(p.coo_approval_recorded_at)}
          </Field>
        </Grid>
      </Section>

      {/* Branding L1 (WI-823) — operator-facing only */}
      <BrandingSection pharmacy={p} onChange={reload} />

      {/* WI-897: People — staff + pending invitations + admin-trigger reset */}
      <PeopleSection pharmacyId={p.id} pharmacyState={p.onboarding_state} />

      {/* Documents — BAA, COO approval, etc. (WI-822) */}
      <DocumentsSection pharmacyId={p.id} />

      {/* Tenant administration controls */}
      <Section title="Tenant administration">
        {p.onboarding_state !== 'suspended' ? (
          <button onClick={async () => {
            if (!confirm('Suspending this pharmacy stops PioneerRx ingest immediately and blocks all logins from this tenant. Continue?')) return
            try { await api.suspendPharmacy(p.id); await reload() }
            catch (e) { setError(friendlyError(e)) }
          }} style={dangerBtnStyle}>
            <Pause size={13} /> Suspend pharmacy
          </button>
        ) : (
          <button onClick={async () => {
            try { await api.reactivatePharmacy(p.id); await reload() }
            catch (e) { setError(friendlyError(e)) }
          }} style={primaryBtnStyle}>
            <Play size={13} /> Reactivate pharmacy
          </button>
        )}

      </Section>

      <PharmacySsoConfig pharmacyId={p.id} />

      <PharmacySenderDomainCard pharmacyId={p.id} />

      <PharmacyMailServerCard pharmacyId={p.id} />

      <PharmacyEmailTemplatesCard pharmacyId={p.id} />

      {/* D-SHIP-19: per-pharmacy POD blob storage — provision gate + live backfill progress */}
      <PharmacyPodStorageCard pharmacyId={p.id} />

      {/* CareRx and its sub-switches live on the CareRx tab (D-CRX-87). */}
      <PharmacyModulesCard pharmacyId={p.id} excludeFamilies={['carerx']} title="Modules"
        subtitle="Enable optional modules for this pharmacy, or start a 90-day trial. Trials expire automatically — no further action needed." />

      {/* D-CRX-61: seed a newly-enabled CareRx tenant's formulary from its own dispensing
          history, so the module is populated on day one rather than looking empty. */}
      <PharmacyCareRxBackfillCard pharmacyId={p.id} />
      </>}
      </div>
    </div>
  )
}

/* ----- Next action card (state-driven) ----- */

function NextActionCard({ detail, onInvited, onCredsGenerated, onReload }: {
  detail: api.PharmacyDetail
  onInvited: (r: { link: string; email: string; expires_at: string }) => void
  onCredsGenerated: (r: api.CredentialsResponse) => void
  onReload: () => void
}) {
  const p = detail.pharmacy
  if (p.onboarding_state === 'pending_provision') {
    return <ProvisioningCard onReload={onReload} provisioning={detail.provisioning ?? null} />
  }
  if (p.onboarding_state === 'pending_admin') {
    return <InviteAdminCard pharmacyId={p.id} alreadyInvited={false} onDone={onInvited} />
  }
  if (p.onboarding_state === 'pending_ingest_setup' || (p.onboarding_state === 'active' && !detail.active_credentials)) {
    return <GenerateCredsCard pharmacyId={p.id} webhookUrl={detail.webhook_url} onDone={onCredsGenerated} />
  }
  if (p.onboarding_state === 'active' && detail.active_credentials) {
    return <ActiveCredsCard
      cred={detail.active_credentials}
      webhookUrl={detail.webhook_url}
      pharmacyId={p.id}
      onRotated={onCredsGenerated}
      onReload={onReload}
    />
  }
  if (p.onboarding_state === 'suspended') {
    return <SuspendedCard />
  }
  return null
}

function ProvisioningCard({ onReload, provisioning }: {
  onReload: () => void
  provisioning: { status: string; failure_reason: string | null; updated_at: string } | null
}) {
  // Provisioning runs automatically in the background once the pharmacy is created — the worker
  // creates the dedicated DB, applies + tenantizes the schema, encrypts it with a Key Vault key, and
  // activates routing, then the pharmacy advances to "Pending admin". No action is needed here; this
  // card just polls until that happens. If the worker hit an error, the registry row says so —
  // show it (with the reason) instead of spinning forever.
  useAutoRefresh(onReload, { intervalMs: 15000 })
  if (provisioning?.status === 'failed') {
    return (
      <div style={{
        border: '1px solid #fecaca', borderRadius: 8, padding: 16, background: '#fef2f2',
      }}>
        <strong style={{ fontSize: 14, color: '#b91c1c' }}>Provisioning failed</strong>
        <p style={{ fontSize: 13, color: '#991b1b', margin: '6px 0 0', lineHeight: 1.5 }}>
          The automatic database setup hit an error and will keep retrying every minute. If this
          persists, share the error below with platform support.
        </p>
        {provisioning.failure_reason ? (
          <pre style={{
            fontSize: 12, color: '#7f1d1d', background: '#fee2e2', borderRadius: 6,
            padding: '8px 10px', margin: '10px 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          }}>{provisioning.failure_reason}</pre>
        ) : null}
        <p style={{ fontSize: 12, color: '#991b1b', margin: '8px 0 0' }}>
          Last attempt: {new Date(provisioning.updated_at).toLocaleString()}
        </p>
      </div>
    )
  }
  return (
    <div style={{
      border: '1px solid #ddd6fe', borderRadius: 8, padding: 16, background: '#faf5ff',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <div style={{
          width: 14, height: 14, borderRadius: '50%', border: '2px solid #6d28d9',
          borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite',
        }} />
        <strong style={{ fontSize: 14, color: '#5b21b6' }}>Provisioning dedicated database…</strong>
      </div>
      <p style={{ fontSize: 13, color: '#6b21a8', margin: '4px 0 0', lineHeight: 1.5 }}>
        Setting up this pharmacy's own encrypted database (own DB → schema → Key Vault encryption →
        routing). This runs automatically and usually takes a few minutes. The page refreshes itself;
        once it's done, the pharmacy moves to <strong>Pending admin</strong> and you can invite the
        first admin.
      </p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

function InviteAdminCard({ pharmacyId, alreadyInvited, onDone }: {
  pharmacyId: string
  alreadyInvited: boolean
  onDone: (r: { link: string; email: string; expires_at: string }) => void
}) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function send() {
    setBusy(true); setError(null)
    try {
      const r = await api.invitePharmacyAdmin(pharmacyId, email, name || undefined)
      onDone({
        link: window.location.origin + r.invite_link,
        email: r.email,
        expires_at: r.expires_at,
      })
      setEmail(''); setName('')
    } catch (e) { setError(friendlyError(e)) }
    finally { setBusy(false) }
  }
  return (
    <ActionCard
      icon={<Mail size={18} />}
      title={alreadyInvited ? "Send another admin invite (optional)" : "Step 1 — Invite the pharmacy's primary admin"}
      subtitle="They'll receive a link to set their password and sign in. Once they're in, they can invite the rest of their team and configure roles."
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
        <input type="email" placeholder="admin@pharmacy.example"
          value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} />
        <input placeholder="Display name (optional)"
          value={name} onChange={e => setName(e.target.value)} style={inputStyle} />
      </div>
      {error && <div style={cardErrorBox}><AlertCircle size={13} /> {error}</div>}
      <button onClick={send} disabled={!email || busy} style={primaryBtnStyle}>
        {busy && <Loader2 size={13} className="spin" />} Send invite
      </button>
    </ActionCard>
  )
}

function GenerateCredsCard({ pharmacyId, webhookUrl, onDone }: {
  pharmacyId: string
  webhookUrl: string | null
  onDone: (r: api.CredentialsResponse) => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  return (
    <ActionCard
      icon={<KeyRound size={18} />}
      title="Step 2 — Generate PioneerRx ingest credentials"
      subtitle="Generates a webhook URL + Basic Auth username/password the pharmacy hands to PioneerRx. The password is shown once and never stored in plaintext."
    >
      {webhookUrl && (
        <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginBottom: 8 }}>
          Webhook URL will be: <code style={codeStyle}>{webhookUrl}</code>
        </div>
      )}
      {error && <div style={cardErrorBox}><AlertCircle size={13} /> {error}</div>}
      <button onClick={async () => {
        setBusy(true); setError(null)
        try { const r = await api.generateIngestCredentials(pharmacyId); onDone(r) }
        catch (e) { setError(friendlyError(e)) }
        finally { setBusy(false) }
      }} disabled={busy} style={primaryBtnStyle}>
        {busy && <Loader2 size={13} className="spin" />} Generate credentials
      </button>
    </ActionCard>
  )
}

function ActiveCredsCard({ cred, webhookUrl, pharmacyId, onRotated }: {
  cred: NonNullable<api.PharmacyDetail['active_credentials']>
  webhookUrl: string | null
  pharmacyId: string
  onRotated: (r: api.CredentialsResponse) => void
  onReload: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  return (
    <ActionCard
      icon={<CheckCircle2 size={18} />}
      title="Pharmacy active — ingest credentials in use"
      subtitle="The pharmacy is fully onboarded. The credentials below are recognized by the ingest endpoint."
      tone="success"
    >
      <Grid>
        <Field label="Webhook URL"><code style={codeStyle}>{webhookUrl}</code></Field>
        <Field label="Username"><code style={codeStyle}>{cred.username}</code></Field>
        <Field label="Generated">{formatUsTimestamp(cred.created_at)}</Field>
        <Field label="Last used">{cred.last_used_at ? formatUsTimestamp(cred.last_used_at) : 'Never'}</Field>
      </Grid>
      {error && <div style={cardErrorBox}><AlertCircle size={13} /> {error}</div>}
      <div style={{ marginTop: 12 }}>
        <button onClick={async () => {
          if (!confirm('Rotating creates a new credential. The old one keeps working for 24 hours so PioneerRx can switch over. Continue?')) return
          setBusy(true); setError(null)
          try { const r = await api.rotateIngestCredentials(pharmacyId); onRotated(r) }
          catch (e) { setError(friendlyError(e)) }
          finally { setBusy(false) }
        }} disabled={busy} style={secondaryBtnStyle}>
          {busy ? <Loader2 size={13} className="spin" /> : <RotateCw size={13} />} Rotate credentials
        </button>
      </div>
    </ActionCard>
  )
}

function SuspendedCard() {
  return (
    <ActionCard icon={<AlertCircle size={18} />} title="Pharmacy is suspended" tone="warning"
      subtitle="No PioneerRx events will be accepted. Existing users from this pharmacy cannot sign in. Reactivate from the bottom of this page when ready." />
  )
}

/* ----- Reveal cards (one-time secrets) ----- */

function CredentialsRevealCard({ creds, onDismiss }: { creds: api.CredentialsResponse; onDismiss: () => void }) {
  return (
    <div style={revealCardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h3 style={revealTitleStyle}><KeyRound size={15} aria-hidden="true" /> PioneerRx ingest credentials generated</h3>
          <p style={revealNoteStyle}>{creds.warning}</p>
        </div>
        <button onClick={onDismiss} style={dismissBtnStyle}>Dismiss</button>
      </div>
      <div style={{ marginTop: 12 }}>
        <RevealRow label="Webhook URL" value={creds.webhook_url} />
        <RevealRow label="Username" value={creds.username} />
        <RevealRow label="Password" value={creds.password} mono />
        {creds.grace_period_until && (
          <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginTop: 8 }}>
            The previous credential remains valid until {formatUsTimestamp(creds.grace_period_until)} to allow PioneerRx switch-over.
          </div>
        )}
      </div>
    </div>
  )
}

function InviteRevealCard({ invite, onDismiss }: { invite: { link: string; email: string; expires_at: string }; onDismiss: () => void }) {
  return (
    <div style={revealCardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h3 style={revealTitleStyle}><Mail size={15} aria-hidden="true" /> Invitation sent to {invite.email}</h3>
          <p style={revealNoteStyle}>
            Invite link below — copy and send it to the pharmacy admin (SMTP delivery is not yet wired in this environment).
            Link expires {formatUsTimestamp(invite.expires_at)}.
          </p>
        </div>
        <button onClick={onDismiss} style={dismissBtnStyle}>Dismiss</button>
      </div>
      <div style={{ marginTop: 12 }}>
        <RevealRow label="Invite link" value={invite.link} />
      </div>
    </div>
  )
}

function RevealRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  const [copied, setCopied] = useState(false)
  function copy() {
    navigator.clipboard.writeText(value)
    setCopied(true); setTimeout(() => setCopied(false), 1200)
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
      <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: '#854d0e', minWidth: 100 }}>{label}</span>
      <code style={{ flex: 1, fontFamily: mono ? 'monospace' : 'inherit', background: '#fff', padding: '6px 10px', borderRadius: 4, border: '1px solid #fde68a', fontSize: 12, wordBreak: 'break-all' as const }}>
        {value}
      </code>
      <button onClick={copy} style={copyBtnStyle}>
        <Copy size={12} /> {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  )
}

/* ----- Editable Pharmacy section (WI-821) ----- */

function UrlCodeField({ pharmacyId, slug, onSaved }: { pharmacyId: string; slug: string | null; onSaved: () => void }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(slug ?? '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const origin = pharmacyAppOrigin()
  if (!editing) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <code style={codeStyleLocal}>{slug ?? '—'}</code>
        {slug && <span style={{ fontSize: 11, color: 'var(--color-text-mute)' }}>{origin}/p/{slug}/login</span>}
        <button type="button" style={linkBtnLocal} onClick={() => { setValue(slug ?? ''); setEditing(true) }}>Edit</button>
      </span>
    )
  }
  const save = async () => {
    setBusy(true); setErr(null)
    try { await api.setPharmacyUrlCode(pharmacyId, value); setEditing(false); onSaved() }
    catch (e: any) { setErr(friendlyError(e)) } finally { setBusy(false) }
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <input value={value} onChange={e => setValue(e.target.value)} placeholder="e.g. ASP" style={{ padding: '5px 8px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, width: 140 }} />
      <button type="button" disabled={busy || !value.trim()} style={primaryBtnStyle} onClick={save}>{busy ? '…' : 'Save'}</button>
      <button type="button" style={linkBtnLocal} onClick={() => setEditing(false)}>Cancel</button>
      {err && <span style={{ color: '#dc2626', fontSize: 12 }}>{err}</span>}
    </span>
  )
}
const linkBtnLocal: React.CSSProperties = { background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: 0 }

function BaaSection({ pharmacy, onSaved }: {
  pharmacy: api.PharmacyDetail['pharmacy']
  onSaved: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState({
    baaReference: pharmacy.baa_reference ?? '',
    baaEffectiveDate: (pharmacy.baa_effective_date ?? '').slice(0, 10),
    baaExpiryDate: (pharmacy.baa_expiry_date ?? '').slice(0, 10),
  })

  async function save() {
    setBusy(true); setError(null)
    try {
      await api.updatePharmacyBaa(pharmacy.id, {
        baaReference: form.baaReference,
        baaEffectiveDate: form.baaEffectiveDate || null,
        baaExpiryDate: form.baaExpiryDate || null,
      })
      setEditing(false)
      onSaved()
    } catch (e) { setError(friendlyError(e)) }
    finally { setBusy(false) }
  }

  if (!editing) {
    return (
      <Section title="Business Associate Agreement" tone="compliance"
        action={
          <button onClick={() => {
            setForm({
              baaReference: pharmacy.baa_reference ?? '',
              baaEffectiveDate: (pharmacy.baa_effective_date ?? '').slice(0, 10),
              baaExpiryDate: (pharmacy.baa_expiry_date ?? '').slice(0, 10),
            })
            setEditing(true)
          }} style={editBtnStyle}>
            <Pencil size={12} /> Edit
          </button>
        }>
        <Grid>
          <Field label="BAA reference"><code style={codeStyle}>{pharmacy.baa_reference}</code></Field>
          <Field label="Effective">{pharmacy.baa_effective_date ? formatUsDate(pharmacy.baa_effective_date) : '—'}</Field>
          <Field label="Expires">{pharmacy.baa_expiry_date ? formatUsDate(pharmacy.baa_expiry_date) : '—'}</Field>
        </Grid>
      </Section>
    )
  }

  return (
    <Section title="Business Associate Agreement" tone="compliance"
      action={
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => { setEditing(false); setError(null) }} disabled={busy}
            style={{ ...editBtnStyle, color: 'var(--color-text-soft)' }}>
            <X size={12} /> Cancel
          </button>
          <button onClick={save} disabled={busy || !form.baaReference.trim()}
            style={{ ...editBtnStyle, background: 'var(--color-primary, #0d9488)', color: '#fff', borderColor: 'var(--color-primary, #0d9488)' }}>
            {busy ? <Loader2 size={12} className="spin" /> : <Save size={12} />} Save
          </button>
        </div>
      }>
      <div style={{ marginBottom: 10, padding: '6px 10px', background: '#fef3c7', color: '#854d0e', borderRadius: 4, fontSize: 11 }}>
        Updates the recorded reference + effective window (audited). Executed agreement documents are
        versioned in the Documents section below.
      </div>
      {error && <div style={cardErrorBoxLocal}><AlertCircle size={13} /> {error}</div>}
      <div style={editGridStyle}>
        <EditField label="BAA reference *">
          <input value={form.baaReference} onChange={e => setForm(s => ({ ...s, baaReference: e.target.value }))} style={inputStyleLocal} />
        </EditField>
        <EditField label="Effective">
          <input type="date" value={form.baaEffectiveDate}
            onChange={e => setForm(s => ({ ...s, baaEffectiveDate: e.target.value }))} style={inputStyleLocal} />
        </EditField>
        <EditField label="Expires">
          <input type="date" value={form.baaExpiryDate}
            onChange={e => setForm(s => ({ ...s, baaExpiryDate: e.target.value }))} style={inputStyleLocal} />
        </EditField>
      </div>
    </Section>
  )
}

function PharmacySection({ pharmacy, onSaved }: {
  pharmacy: api.PharmacyDetail['pharmacy']
  onSaved: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState<api.UpdatePharmacyInput>({
    name: pharmacy.name,
    npi: pharmacy.npi ?? '',
    ncpdp: pharmacy.ncpdp ?? '',
    dea: pharmacy.dea ?? '',
    email: pharmacy.email ?? '',
    primaryPhone: pharmacy.primary_phone ?? '',
    addressLine: pharmacy.address_line ?? '',
    city: pharmacy.city ?? '',
    stateCode: pharmacy.state_code ?? '',
    zipCode: pharmacy.zip_code ?? '',
    branchLabel: pharmacy.branch_label ?? '',
    ssoProvider: (pharmacy.sso_provider as api.UpdatePharmacyInput['ssoProvider']) ?? 'none',
    storagePricePerGbCents: pharmacy.storage_price_per_gb_cents ?? null,
    storageIncludedGb: pharmacy.storage_included_gb ?? 10,
  })

  function set<K extends keyof api.UpdatePharmacyInput>(k: K, v: api.UpdatePharmacyInput[K]) {
    setForm(s => ({ ...s, [k]: v }))
  }

  async function save() {
    setBusy(true); setError(null)
    try {
      await api.updatePharmacy(pharmacy.id, form)
      setEditing(false)
      onSaved()
    } catch (e) { setError(friendlyError(e)) }
    finally { setBusy(false) }
  }

  if (!editing) {
    return (
      <Section title="Pharmacy"
        action={
          <button onClick={() => setEditing(true)} style={editBtnStyle}>
            <Pencil size={12} /> Edit
          </button>
        }>
        <Grid>
          <Field label="URL code"><UrlCodeField pharmacyId={pharmacy.id} slug={pharmacy.slug ?? null} onSaved={onSaved} /></Field>
          <Field label="Login URL">
            {(pharmacy.url_code || pharmacy.slug) ? (
              <code style={codeStyleLocal}>
                {pharmacyAppOrigin()}/p/{pharmacy.url_code ?? pharmacy.slug}/login
              </code>
            ) : '—'}
          </Field>
          <Field label="NPI">{pharmacy.npi || '—'}</Field>
          <Field label="NCPDP">{pharmacy.ncpdp || '—'}</Field>
          <Field label="Storage">
            {(() => {
              const used = pharmacy.storage_used_bytes ?? 0
              const quota = pharmacy.storage_quota_bytes ?? 0
              const gb = (b: number) => b / 1073741824
              const pct = quota > 0 ? Math.min(100, Math.round((used / quota) * 100)) : 0
              const color = pct >= 95 ? '#dc2626' : pct >= 80 ? '#d97706' : 'var(--color-primary)'
              const included = pharmacy.storage_included_gb ?? 10
              const priceCents = pharmacy.storage_price_per_gb_cents
              const billableGb = Math.max(0, gb(used) - included)
              const costCents = priceCents != null ? Math.round(billableGb * priceCents) : null
              return (
                <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 4, minWidth: 170 }}>
                  <span style={{ fontSize: 13 }}>{gb(used).toFixed(2)} / {gb(quota).toFixed(0)} GB <span style={{ color: 'var(--color-text-mute)' }}>({pct}%)</span></span>
                  <span style={{ height: 6, background: 'var(--color-border)', borderRadius: 3, overflow: 'hidden' }}>
                    <span style={{ display: 'block', height: '100%', width: `${pct}%`, background: color }} />
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--color-text-soft)' }}>
                    First {included} GB included{priceCents != null
                      ? ` · $${(priceCents / 100).toFixed(2)}/GB · est. $${((costCents ?? 0) / 100).toFixed(2)} (${billableGb.toFixed(2)} GB billable)`
                      : ' · price not set'}
                  </span>
                </span>
              )
            })()}
          </Field>
          <Field label="DEA">{pharmacy.dea || '—'}</Field>
          <Field label="Email">{pharmacy.email || '—'}</Field>
          <Field label="Phone">{pharmacy.primary_phone || '—'}</Field>
          <Field label="SSO">{pharmacy.sso_provider}</Field>
          <Field label="Created">{new Date(pharmacy.created_at).toLocaleString()}</Field>
        </Grid>
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--color-text-soft)' }}>
          {[pharmacy.address_line, pharmacy.city, pharmacy.state_code, pharmacy.zip_code].filter(Boolean).join(', ') || 'No address'}
        </div>
      </Section>
    )
  }

  return (
    <Section title="Pharmacy"
      action={
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => { setEditing(false); setError(null) }} disabled={busy}
            style={{ ...editBtnStyle, color: 'var(--color-text-soft)' }}>
            <X size={12} /> Cancel
          </button>
          <button onClick={save} disabled={busy} style={{ ...editBtnStyle, background: 'var(--color-primary, #0d9488)', color: '#fff', borderColor: 'var(--color-primary, #0d9488)' }}>
            {busy ? <Loader2 size={12} className="spin" /> : <Save size={12} />} Save
          </button>
        </div>
      }>
      <div style={{ marginBottom: 10, padding: '6px 10px', background: '#fef3c7', color: '#854d0e', borderRadius: 4, fontSize: 11 }}>
        Slug, COO approval, and parent organization cannot be edited here. Slug is in the webhook URL; COO approval is recorded once. The BAA is edited on its own card below.
      </div>
      {error && <div style={cardErrorBoxLocal}><AlertCircle size={13} /> {error}</div>}
      <div style={editGridStyle}>
        <EditField label="Name *">
          <input value={form.name ?? ''} onChange={e => set('name', e.target.value)} style={inputStyleLocal} />
        </EditField>
        <EditField label="Branch label">
          <input value={form.branchLabel ?? ''} onChange={e => set('branchLabel', e.target.value)} style={inputStyleLocal} />
        </EditField>
        <EditField label="NPI">
          <input value={form.npi ?? ''} onChange={e => set('npi', e.target.value)} style={inputStyleLocal} />
        </EditField>
        <EditField label="NCPDP">
          <input value={form.ncpdp ?? ''} onChange={e => set('ncpdp', e.target.value)} style={inputStyleLocal} />
        </EditField>
        <EditField label="DEA">
          <input value={form.dea ?? ''} onChange={e => set('dea', e.target.value)} style={inputStyleLocal} />
        </EditField>
        <EditField label="Storage $/GB">
          <input type="number" min="0" step="0.01"
            value={form.storagePricePerGbCents != null ? (form.storagePricePerGbCents / 100).toString() : ''}
            onChange={e => set('storagePricePerGbCents', e.target.value === '' ? null : Math.max(0, Math.round(parseFloat(e.target.value) * 100)))}
            style={inputStyleLocal} placeholder="e.g. 0.15" />
        </EditField>
        <EditField label="Storage included GB">
          <input type="number" min="0" step="1"
            value={form.storageIncludedGb != null ? form.storageIncludedGb.toString() : ''}
            onChange={e => set('storageIncludedGb', e.target.value === '' ? null : Math.max(0, Math.round(parseFloat(e.target.value))))}
            style={inputStyleLocal} placeholder="10" />
        </EditField>
        <EditField label="Email">
          <input type="email" value={form.email ?? ''} onChange={e => set('email', e.target.value)} style={inputStyleLocal} />
        </EditField>
        <EditField label="Phone">
          <input type="tel" value={form.primaryPhone ?? ''} onChange={e => set('primaryPhone', e.target.value)} style={inputStyleLocal} />
        </EditField>
        <EditField label="SSO">
          <select value={form.ssoProvider ?? 'none'} onChange={e => set('ssoProvider', e.target.value as api.UpdatePharmacyInput['ssoProvider'])} style={inputStyleLocal}>
            <option value="none">None</option>
            <option value="okta">Okta</option>
            <option value="azure_ad">Microsoft Entra (Azure AD)</option>
            <option value="google_workspace">Google Workspace</option>
          </select>
        </EditField>
      </div>
      <EditField label="Address">
        <input value={form.addressLine ?? ''} onChange={e => set('addressLine', e.target.value)} style={inputStyleLocal} />
      </EditField>
      <div style={editGridStyle}>
        <EditField label="City">
          <input value={form.city ?? ''} onChange={e => set('city', e.target.value)} style={inputStyleLocal} />
        </EditField>
        <EditField label="State">
          <input value={form.stateCode ?? ''} onChange={e => set('stateCode', e.target.value.toUpperCase())} maxLength={2} style={inputStyleLocal} />
        </EditField>
        <EditField label="Zip">
          <input value={form.zipCode ?? ''} onChange={e => set('zipCode', e.target.value)} style={inputStyleLocal} />
        </EditField>
      </div>
    </Section>
  )
}

function EditField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block', marginBottom: 8 }}>
      <div style={{ fontSize: 11, color: 'var(--color-text-mute)', textTransform: 'uppercase' as const, letterSpacing: 0.4, marginBottom: 3 }}>{label}</div>
      {children}
    </label>
  )
}

const editBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '4px 10px', fontSize: 12, fontWeight: 500,
  background: 'transparent', color: 'var(--color-text-soft)',
  border: '1px solid var(--color-border)', borderRadius: 4,
  cursor: 'pointer', fontFamily: 'inherit',
}
const inputStyleLocal: React.CSSProperties = {
  width: '100%', padding: '6px 8px', fontSize: 13,
  border: '1px solid var(--color-border)', borderRadius: 4,
  background: 'var(--color-surface)', fontFamily: 'inherit', boxSizing: 'border-box',
}
const editGridStyle: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 10,
}
const codeStyleLocal: React.CSSProperties = {
  fontFamily: 'monospace', fontSize: 11,
  background: 'var(--color-surface-alt)', padding: '1px 5px', borderRadius: 3,
}
const cardErrorBoxLocal: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
  background: '#fee2e2', color: '#991b1b', borderRadius: 4, fontSize: 12, marginBottom: 8,
}

/* ----- Layout primitives ----- */

function Section({ title, tone, action, children }: { title: string; tone?: 'compliance'; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{
      background: 'var(--color-surface, #fff)',
      border: tone === 'compliance' ? '1px solid #fbbf24' : '1px solid var(--color-border)',
      borderRadius: 8, padding: 16, marginBottom: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ fontSize: 13, fontWeight: 600, margin: 0,
          color: tone === 'compliance' ? '#854d0e' : undefined,
          display: 'flex', alignItems: 'center', gap: 6 }}>
          {tone === 'compliance' && <ShieldCheck size={13} />}
          {title}
        </h3>
        {action}
      </div>
      {children}
    </div>
  )
}
function ActionCard({ title, subtitle, icon, tone, children }: {
  title: string; subtitle?: string; icon?: React.ReactNode
  tone?: 'success' | 'warning'
  children?: React.ReactNode
}) {
  const bg = tone === 'success' ? '#f0fdf4' : tone === 'warning' ? '#fef3c7' : '#f0f9ff'
  const border = tone === 'success' ? '#86efac' : tone === 'warning' ? '#fde68a' : '#bae6fd'
  return (
    <div style={{
      background: bg, border: `1px solid ${border}`, borderRadius: 8,
      padding: 16, marginBottom: 12,
    }}>
      <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
        {icon} {title}
      </h3>
      {subtitle && <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--color-text-soft)' }}>{subtitle}</p>}
      {children}
    </div>
  )
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, color: 'var(--color-text-mute)', textTransform: 'uppercase' as const, letterSpacing: 0.4, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, overflowWrap: 'anywhere', minWidth: 0 }}>{children}</div>
    </div>
  )
}
function Grid({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>{children}</div>
}
function StateBadge({ state }: { state: api.PharmacyListItem['onboarding_state'] }) {
  const config = {
    pending_baa:           { color: '#a16207', bg: '#fef3c7', label: 'Pending BAA' },
    pending_provision:     { color: '#6d28d9', bg: '#ede9fe', label: 'Provisioning database' },
    pending_admin:         { color: '#1e40af', bg: '#dbeafe', label: 'Pending admin' },
    pending_ingest_setup:  { color: '#0891b2', bg: '#cffafe', label: 'Needs creds' },
    active:                { color: '#166534', bg: '#dcfce7', label: 'Active' },
    suspended:             { color: '#991b1b', bg: '#fee2e2', label: 'Suspended' },
  }[state] ?? { color: '#475569', bg: '#f1f5f9', label: state }
  return (
    <div style={{
      padding: '4px 10px', borderRadius: 4, background: config.bg, color: config.color,
      fontSize: 11, fontWeight: 600, letterSpacing: 0.3, whiteSpace: 'nowrap',
    }}>{config.label}</div>
  )
}

const backLinkStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  background: 'transparent', border: 'none', cursor: 'pointer',
  color: 'var(--color-text-soft)', fontSize: 13, padding: '4px 0', marginBottom: 12,
  fontFamily: 'inherit',
}
const titleRowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }
const h2Style: React.CSSProperties = { fontSize: 22, fontWeight: 700, margin: 0, fontFamily: 'Georgia, serif' }
const inputStyle: React.CSSProperties = {
  padding: '8px 10px', fontSize: 13,
  border: '1px solid var(--color-border)', borderRadius: 4,
  background: 'var(--color-surface)', fontFamily: 'inherit', boxSizing: 'border-box',
}
const codeStyle: React.CSSProperties = {
  fontFamily: 'monospace', fontSize: 11,
  background: 'var(--color-surface-alt)', padding: '1px 5px', borderRadius: 3, wordBreak: 'break-all' as const,
}
const primaryBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', fontSize: 13, fontWeight: 500,
  background: 'var(--color-primary, #0d9488)', color: '#fff', border: 'none',
  borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit',
}
const secondaryBtnStyle: React.CSSProperties = {
  ...primaryBtnStyle, background: 'transparent', color: 'var(--color-text-soft)',
  border: '1px solid var(--color-border)',
}
const dangerBtnStyle: React.CSSProperties = {
  ...primaryBtnStyle, background: 'transparent', color: '#991b1b', border: '1px solid #fca5a5',
}
const dismissBtnStyle: React.CSSProperties = {
  background: 'transparent', border: 'none', cursor: 'pointer',
  color: '#854d0e', fontSize: 12, fontFamily: 'inherit', textDecoration: 'underline',
}
const copyBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  background: 'transparent', border: '1px solid #fbbf24', color: '#854d0e',
  padding: '4px 8px', fontSize: 11, borderRadius: 3, cursor: 'pointer', fontFamily: 'inherit',
}
const cardErrorBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
  background: '#fee2e2', color: '#991b1b', borderRadius: 4, fontSize: 12, marginBottom: 8,
}
const errorBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
  background: '#fee2e2', color: '#991b1b', borderRadius: 4, fontSize: 12,
}
const loadingBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: 24, color: 'var(--color-text-soft)', fontSize: 14 }
const revealCardStyle: React.CSSProperties = {
  background: '#fffbeb', border: '2px solid #fbbf24', borderRadius: 8, padding: 16, marginBottom: 12,
}
const revealTitleStyle: React.CSSProperties = {
  fontSize: 14, fontWeight: 600, margin: '0 0 4px', color: '#854d0e',
  display: 'flex', alignItems: 'center', gap: 6,
}
const revealNoteStyle: React.CSSProperties = { margin: 0, fontSize: 12, color: '#854d0e' }

// =====================================================================
// Documents — BAA + COO approval, with versioning + uploads (WI-822)
// =====================================================================
function DocumentsSection({ pharmacyId }: { pharmacyId: string }) {
  const [docs, setDocs] = useState<api.PharmacyDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showUpload, setShowUpload] = useState(false)
  const [supersedesDoc, setSupersedesDoc] = useState<api.PharmacyDocument | null>(null)

  async function reload() {
    setLoading(true); setError(null)
    try {
      const r = await api.listPharmacyDocuments(pharmacyId)
      setDocs(r.items)
    } catch (e) { setError(friendlyError(e)) }
    finally { setLoading(false) }
  }
  useEffect(() => { reload() }, [pharmacyId])

  const grouped: Record<string, api.PharmacyDocument[]> = { baa: [], coo_approval: [], notice_of_privacy: [], other: [] }
  docs.forEach(d => grouped[d.kind].push(d))

  return (
    <Section
      title="Documents"
      action={
        <button onClick={() => { setSupersedesDoc(null); setShowUpload(true) }} style={primaryBtnStyleLocal}>
          <Plus size={13} /> Upload document
        </button>
      }
    >
      {error && <div style={cardErrorBoxLocal}><AlertCircle size={13} /> {error}</div>}

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-text-soft)', fontSize: 13 }}>
          <Loader2 size={14} className="spin" /> Loading…
        </div>
      ) : docs.length === 0 ? (
        <div style={{ padding: 16, textAlign: 'center', fontSize: 13, color: 'var(--color-text-soft)', border: '1px dashed var(--color-border)', borderRadius: 6 }}>
          No documents uploaded. The BAA and engagement approval reference text on this page were recorded at onboarding;
          uploading the actual signed PDFs here gives auditors something concrete to point at.
        </div>
      ) : (
        <>
          {(['baa', 'coo_approval', 'notice_of_privacy', 'other'] as const).map(kind =>
            grouped[kind].length === 0 ? null : (
              <div key={kind} style={{ marginBottom: 12 }}>
                <h4 style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--color-text-mute)', margin: '0 0 6px' }}>
                  {KIND_LABELS[kind]}
                </h4>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {grouped[kind].map(d => (
                    <DocumentRow
                      key={d.id}
                      doc={d}
                      onRenew={() => { setSupersedesDoc(d); setShowUpload(true) }}
                      onArchive={async () => {
                        if (!confirm(`Archive "${d.title || d.file_name}"? It will stay in audit history but won't be the current document.`)) return
                        try { await api.archivePharmacyDocument(d.id); await reload() }
                        catch (e) { setError(friendlyError(e)) }
                      }}
                    />
                  ))}
                </ul>
              </div>
            )
          )}
        </>
      )}

      {showUpload && (
        <UploadDocumentDialog
          pharmacyId={pharmacyId}
          supersedes={supersedesDoc}
          onClose={() => { setShowUpload(false); setSupersedesDoc(null) }}
          onUploaded={async () => { setShowUpload(false); setSupersedesDoc(null); await reload() }}
        />
      )}
    </Section>
  )
}

const KIND_LABELS: Record<string, string> = {
  baa: 'Business Associate Agreement',
  coo_approval: 'Engagement approval',
  notice_of_privacy: 'Notice of Privacy Practices',
  other: 'Other documents',
}

function DocumentRow({ doc, onRenew, onArchive }: {
  doc: api.PharmacyDocument
  onRenew: () => void
  onArchive: () => void
}) {
  const expiringSoon = doc.expiry_date && new Date(doc.expiry_date) <= new Date(Date.now() + 60 * 86400 * 1000)
  return (
    <li style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      padding: '10px 12px', marginBottom: 4,
      background: doc.archived_at ? 'var(--color-surface-alt)' : 'var(--color-surface)',
      border: '1px solid var(--color-border)', borderRadius: 6,
      opacity: doc.archived_at ? 0.7 : 1,
    }}>
      <FileText size={16} style={{ flexShrink: 0, marginTop: 2, color: doc.is_current ? '#0d9488' : 'var(--color-text-mute)' }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 2 }}>
          <strong style={{ fontSize: 13 }}>{doc.title || doc.file_name}</strong>
          {doc.is_current && <span style={pillCurrent}>Current</span>}
          {doc.archived_at && <span style={pillArchived}>Archived</span>}
          {doc.supersedes_id && <span style={pillVersion}>Renewal</span>}
          {expiringSoon && !doc.archived_at && <span style={pillExpiring}>Expiring soon</span>}
        </div>
        <div style={{ fontSize: 11, color: 'var(--color-text-soft)' }}>
          {doc.file_name} · {(doc.size_bytes / 1024).toFixed(1)} KB · uploaded {new Date(doc.uploaded_at).toLocaleDateString()} by {doc.uploaded_by_name}
        </div>
        {(doc.effective_date || doc.expiry_date) && (
          <div style={{ fontSize: 11, color: 'var(--color-text-soft)', marginTop: 2 }}>
            {doc.effective_date && <>Effective: {doc.effective_date.substring(0, 10)} · </>}
            {doc.expiry_date && <>Expires: {doc.expiry_date.substring(0, 10)}</>}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 4 }}>
        <button
          onClick={async () => {
            try { const { url } = await api.getPharmacyDocumentDownloadUrl(doc.id); window.open(url, '_blank', 'noopener,noreferrer') }
            catch { /* surfaced by the row's error state */ }
          }}
          style={iconBtnStyle}
          title="Download"
        >
          <Download size={13} />
        </button>
        {doc.is_current && doc.kind === 'baa' && (
          <button onClick={onRenew} title="Renew (upload replacement)" style={iconBtnStyle}>
            <Upload size={13} />
          </button>
        )}
        {!doc.archived_at && (
          <button onClick={onArchive} title="Archive" style={iconBtnStyle}>
            <Archive size={13} />
          </button>
        )}
      </div>
    </li>
  )
}

function UploadDocumentDialog({ pharmacyId, supersedes, onClose, onUploaded }: {
  pharmacyId: string
  supersedes: api.PharmacyDocument | null
  onClose: () => void
  onUploaded: () => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [kind, setKind] = useState<'baa' | 'coo_approval' | 'notice_of_privacy' | 'other'>(supersedes?.kind || 'baa')
  const [title, setTitle] = useState(supersedes ? `${supersedes.title || supersedes.file_name} (renewal)` : '')
  const [effectiveDate, setEffectiveDate] = useState('')
  const [expiryDate, setExpiryDate] = useState('')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!file) { setError('Choose a PDF file to upload.'); return }
    setBusy(true); setError(null)
    try {
      await api.uploadPharmacyDocument(pharmacyId, {
        file, kind, title: title || undefined,
        effectiveDate: effectiveDate || undefined,
        expiryDate: expiryDate || undefined,
        supersedesId: supersedes?.id,
        notes: notes || undefined,
      })
      onUploaded()
    } catch (e) { setError(friendlyError(e)); setBusy(false) }
  }

  return (
    <div style={dialogOverlayStyle} onClick={onClose}>
      <div style={dialogStyle} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>
            {supersedes ? `Renew: ${supersedes.title || supersedes.file_name}` : 'Upload document'}
          </h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
            <X size={16} />
          </button>
        </div>
        {supersedes && (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4, padding: 8, fontSize: 12, color: '#854d0e', marginBottom: 12 }}>
            Uploading will mark the new file as the current {KIND_LABELS[supersedes.kind].toLowerCase()}. The previous version stays in history.
          </div>
        )}
        <form onSubmit={submit}>
          <label style={{ display: 'block', marginBottom: 10 }}>
            <div style={dialogFieldLabel}>File (PDF, max 25 MB) *</div>
            <input type="file" accept="application/pdf" onChange={e => setFile(e.target.files?.[0] || null)}
              style={{ width: '100%', fontSize: 13 }} required />
          </label>
          {!supersedes && (
            <label style={{ display: 'block', marginBottom: 10 }}>
              <div style={dialogFieldLabel}>Kind *</div>
              <select value={kind} onChange={e => setKind(e.target.value as typeof kind)} style={dialogInputStyle}>
                <option value="baa">Business Associate Agreement</option>
                <option value="coo_approval">Engagement approval letter</option>
                <option value="notice_of_privacy">Notice of Privacy Practices</option>
                <option value="other">Other</option>
              </select>
            </label>
          )}
          <label style={{ display: 'block', marginBottom: 10 }}>
            <div style={dialogFieldLabel}>Title (optional)</div>
            <input value={title} onChange={e => setTitle(e.target.value)}
              placeholder="e.g. BAA — Apple Specialty 2026-2027"
              style={dialogInputStyle} />
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
            <label>
              <div style={dialogFieldLabel}>Effective date</div>
              <input type="date" value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)} style={dialogInputStyle} />
            </label>
            <label>
              <div style={dialogFieldLabel}>Expiry date</div>
              <input type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)} style={dialogInputStyle} />
            </label>
          </div>
          <label style={{ display: 'block', marginBottom: 10 }}>
            <div style={dialogFieldLabel}>Notes (optional)</div>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} style={dialogInputStyle} />
          </label>

          {error && <div style={cardErrorBoxLocal}><AlertCircle size={13} /> {error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button type="button" onClick={onClose} disabled={busy} style={secondaryBtnStyleLocal}>Cancel</button>
            <button type="submit" disabled={busy || !file} style={primaryBtnStyleLocal}>
              {busy ? <Loader2 size={13} className="spin" /> : <Upload size={13} />} Upload
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const primaryBtnStyleLocal: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '6px 12px', fontSize: 12, fontWeight: 500,
  background: 'var(--color-primary, #0d9488)', color: '#fff', border: 'none',
  borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit',
}
const secondaryBtnStyleLocal: React.CSSProperties = {
  ...primaryBtnStyleLocal, background: 'transparent', color: 'var(--color-text-soft)',
  border: '1px solid var(--color-border)',
}
const iconBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: 'transparent', border: '1px solid var(--color-border)', borderRadius: 4,
  padding: '4px 6px', cursor: 'pointer', color: 'var(--color-text-soft)',
  textDecoration: 'none', fontFamily: 'inherit',
}
const pillCurrent: React.CSSProperties = {
  fontSize: 9, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
  padding: '1px 6px', borderRadius: 8, background: '#dcfce7', color: '#166534',
}
const pillArchived: React.CSSProperties = { ...pillCurrent, background: '#f1f5f9', color: '#475569' }
const pillVersion: React.CSSProperties = { ...pillCurrent, background: '#dbeafe', color: '#1e40af' }
const pillExpiring: React.CSSProperties = { ...pillCurrent, background: '#fef3c7', color: '#92400e' }
const dialogOverlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16,
}
const dialogStyle: React.CSSProperties = {
  background: 'var(--color-surface, #fff)', borderRadius: 8, padding: 20,
  maxWidth: 500, width: '100%', maxHeight: '90vh', overflow: 'auto',
}
const dialogFieldLabel: React.CSSProperties = {
  fontSize: 12, fontWeight: 500, marginBottom: 4, color: 'var(--color-text-soft)',
}
const dialogInputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 8px', fontSize: 13,
  border: '1px solid var(--color-border)', borderRadius: 4,
  background: 'var(--color-surface)', fontFamily: 'inherit', boxSizing: 'border-box',
}

// =====================================================================
// Branding L1 (WI-823) — display name override + logo + accent color
// Operator-facing only; patients/providers continue to see canonical
// OwlRx Portal branding.
// =====================================================================
function BrandingSection({ pharmacy, onChange }: {
  pharmacy: api.PharmacyDetail['pharmacy']
  onChange: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [displayName, setDisplayName] = useState(pharmacy.display_name_override || '')
  const [color, setColor] = useState(pharmacy.primary_color_hex || '#0d9488')
  // D-POT-6: who signs the Letter of Agreement / is the invoice contact (printed on the billing PDFs).
  const [signatoryName, setSignatoryName] = useState(pharmacy.doc_signatory_name || '')
  const [signatoryTitle, setSignatoryTitle] = useState(pharmacy.doc_signatory_title || '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [logoBusy, setLogoBusy] = useState(false)

  async function save() {
    setBusy(true); setError(null)
    try {
      await api.updatePharmacy(pharmacy.id, {
        displayNameOverride: displayName,
        primaryColorHex: color,
        docSignatoryName: signatoryName,
        docSignatoryTitle: signatoryTitle,
      })
      setEditing(false)
      onChange()
    } catch (e) { setError(friendlyError(e)) }
    finally { setBusy(false) }
  }

  function cancel() {
    setDisplayName(pharmacy.display_name_override || '')
    setColor(pharmacy.primary_color_hex || '#0d9488')
    setSignatoryName(pharmacy.doc_signatory_name || '')
    setSignatoryTitle(pharmacy.doc_signatory_title || '')
    setError(null)
    setEditing(false)
  }

  async function uploadLogo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setLogoBusy(true); setError(null)
    try {
      await api.uploadPharmacyLogo(pharmacy.id, file)
      onChange()
    } catch (err) { setError(friendlyError(err)) }
    finally { setLogoBusy(false); e.target.value = '' }
  }

  return (
    <Section
      title="Branding"
      action={!editing && (
        <button onClick={() => setEditing(true)} style={editBtnStyle}>
          <Pencil size={12} /> Edit
        </button>
      )}
    >
      <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--color-text-soft)', lineHeight: 1.5 }}>
        Operator-facing branding only. Pharmacy staff, owners, and admins see this when logged in.
        Patients and providers continue to see the standard portal branding regardless.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 16, alignItems: 'flex-start' }}>
        {/* Logo column */}
        <div style={{ width: 120 }}>
          <div style={{
            width: 120, height: 120,
            border: '1px dashed var(--color-border)', borderRadius: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--color-surface-alt)',
            overflow: 'hidden',
          }}>
            {pharmacy.logo_url
              ? <img src={pharmacy.logo_url} alt="logo" style={{ maxWidth: '100%', maxHeight: '100%' }} />
              : <span style={{ fontSize: 11, color: 'var(--color-text-mute)' }}>No logo</span>
            }
          </div>
          <label style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
            marginTop: 6, padding: '4px 10px', fontSize: 11, fontWeight: 500,
            border: '1px solid var(--color-border)', borderRadius: 4, cursor: 'pointer',
            color: 'var(--color-text-soft)', width: '100%', boxSizing: 'border-box',
          }}>
            {logoBusy ? <Loader2 size={11} className="spin" /> : <Upload size={11} />}
            {logoBusy ? 'Uploading…' : pharmacy.logo_url ? 'Replace' : 'Upload logo'}
            <input type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              onChange={uploadLogo}
              disabled={logoBusy}
              style={{ display: 'none' }} />
          </label>
          <div style={{ fontSize: 10, color: 'var(--color-text-mute)', marginTop: 4, textAlign: 'center' }}>
            PNG, JPG, SVG, WebP · max 2 MB
          </div>
        </div>

        {/* Fields column */}
        <div style={{ flex: 1 }}>
          {editing ? (
            <>
              <label style={{ display: 'block', marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: 'var(--color-text-mute)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
                  Display name override
                </div>
                <input
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  placeholder={pharmacy.name}
                  style={inputStyleLocal}
                />
                <div style={{ fontSize: 11, color: 'var(--color-text-mute)', marginTop: 2 }}>
                  Optional alternate name. Leave empty to use the legal name "{pharmacy.name}".
                </div>
              </label>
              <label style={{ display: 'block', marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: 'var(--color-text-mute)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
                  Primary accent color
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="color"
                    value={color}
                    onChange={e => setColor(e.target.value)}
                    style={{ width: 50, height: 32, padding: 0, border: '1px solid var(--color-border)', borderRadius: 4, cursor: 'pointer' }}
                  />
                  <input
                    value={color}
                    onChange={e => setColor(e.target.value)}
                    placeholder="#0d9488"
                    style={{ ...inputStyleLocal, fontFamily: 'monospace', maxWidth: 130 }}
                  />
                  <span style={{ fontSize: 11, color: 'var(--color-text-mute)' }}>e.g. #0d9488</span>
                </div>
              </label>
              <label style={{ display: 'block', marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: 'var(--color-text-mute)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
                  Document signatory name
                </div>
                <input
                  value={signatoryName}
                  onChange={e => setSignatoryName(e.target.value)}
                  placeholder="Who signs the Letter of Agreement"
                  maxLength={128}
                  style={inputStyleLocal}
                />
                <div style={{ fontSize: 11, color: 'var(--color-text-mute)', marginTop: 2 }}>
                  Printed on the nursing-agency LOA ("By / Name") and as the invoice contact. Empty = a blank line to sign.
                </div>
              </label>
              <label style={{ display: 'block', marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: 'var(--color-text-mute)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
                  Document signatory title
                </div>
                <input
                  value={signatoryTitle}
                  onChange={e => setSignatoryTitle(e.target.value)}
                  placeholder="e.g. Pharmacy Operations"
                  maxLength={128}
                  style={inputStyleLocal}
                />
              </label>
              {error && <div style={cardErrorBoxLocal}><AlertCircle size={13} /> {error}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={save} disabled={busy} style={primaryBtnStyleLocal}>
                  {busy ? <Loader2 size={13} className="spin" /> : <Save size={13} />} Save
                </button>
                <button onClick={cancel} disabled={busy} style={secondaryBtnStyleLocal}>Cancel</button>
              </div>
            </>
          ) : (
            <Grid>
              <Field label="Display name">
                {pharmacy.display_name_override || <span style={{ color: 'var(--color-text-mute)' }}>(legal name "{pharmacy.name}")</span>}
              </Field>
              <Field label="Primary color">
                {pharmacy.primary_color_hex
                  ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: pharmacy.primary_color_hex, border: '1px solid var(--color-border)' }} />
                      <code style={codeStyleLocal}>{pharmacy.primary_color_hex}</code>
                    </span>
                  : <span style={{ color: 'var(--color-text-mute)' }}>Default</span>}
              </Field>
              <Field label="Document signatory">
                {pharmacy.doc_signatory_name
                  ? <>{pharmacy.doc_signatory_name}{pharmacy.doc_signatory_title && <span style={{ color: 'var(--color-text-soft)' }}> · {pharmacy.doc_signatory_title}</span>}</>
                  : <span style={{ color: 'var(--color-text-mute)' }}>Not set — the LOA prints a blank signature line</span>}
              </Field>
            </Grid>
          )}
          {error && !editing && <div style={cardErrorBoxLocal}><AlertCircle size={13} /> {error}</div>}
        </div>
      </div>
    </Section>
  )
}

// ====================================================================
// WI-897: People section — staff + pending invitations on this pharmacy
// ====================================================================

const ASSIGNABLE_ROLES = [
  'Owner',
  'Admin',
  'Manager',
  'Pharmacist (RPh)',
  'Technician',
  'QA',
  'Provider',
  'Provider Nurse',
]

function PeopleSection({ pharmacyId, pharmacyState }: {
  pharmacyId: string
  pharmacyState: string
}) {
  const [data, setData] = useState<{
    users: api.PharmacyUser[]
    pending_invitations: api.PharmacyPendingInvite[]
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showInvite, setShowInvite] = useState(false)
  const [revealedInvite, setRevealedInvite] = useState<{ link: string; email: string; role: string } | null>(null)
  const [revealedReset, setRevealedReset] = useState<{ link: string; email: string } | null>(null)

  async function reload() {
    setLoading(true); setError(null)
    try { setData(await api.listPharmacyUsers(pharmacyId)) }
    catch (e) { setError(friendlyError(e)) }
    finally { setLoading(false) }
  }
  useEffect(() => { reload() }, [pharmacyId])

  async function handleResetPassword(user: api.PharmacyUser) {
    if (!confirm(`Generate a password reset link for ${user.email}?`)) return
    try {
      const r = await api.adminTriggerPasswordReset(user.id)
      setRevealedReset({ link: r.reset_link, email: r.email })
    } catch (e) {
      alert(`Reset failed: ${friendlyError(e)}`)
    }
  }

  return (
    <Section
      title="People"
      action={
        pharmacyState !== 'suspended' && (
          <button
            onClick={() => setShowInvite(true)}
            style={inviteBtnStyle}
            title="Invite a user to this pharmacy"
          >
            <UserPlus size={13} aria-hidden="true" /> Invite user
          </button>
        )
      }
    >
      {revealedInvite && (
        <RevealCard
          title={`Invitation link for ${revealedInvite.email} (${revealedInvite.role})`}
          link={revealedInvite.link}
          onDismiss={() => setRevealedInvite(null)}
          note="Copy this link and send it to the invitee. SMTP delivery is also queued."
        />
      )}
      {revealedReset && (
        <RevealCard
          title={`Password reset link for ${revealedReset.email}`}
          link={revealedReset.link}
          onDismiss={() => setRevealedReset(null)}
          note="Copy this link and send it to the user. The link is single-use and expires in 60 minutes."
        />
      )}

      {error && <div style={cardErrorBoxLocal}><AlertCircle size={13} /> {error}</div>}

      {loading ? (
        <div style={{ padding: '12px 0', color: 'var(--color-text-soft)', fontSize: 13 }}>
          <Loader2 size={14} className="spin" /> Loading…
        </div>
      ) : (
        <>
          {/* Active accounts */}
          {(data?.users?.length ?? 0) === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--color-text-mute)', padding: '4px 0' }}>
              No accounts yet on this pharmacy.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {data!.users.map(u => (
                <div key={u.id} style={userRowStyle}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>
                      {u.display_name}
                      {u.is_synthetic && <span style={syntheticBadgeStyle}>demo</span>}
                      <span style={statusBadgeStyle(u.status)}>{u.status}</span>
                    </div>
                    <div className="email-text" style={{ fontSize: 12, color: 'var(--color-text-soft)' }}>
                      {u.email}
                      {u.last_login_at
                        ? ` · last login ${new Date(u.last_login_at).toLocaleDateString()}`
                        : ' · never logged in'}
                    </div>
                  </div>
                  {u.status === 'active' && (u as { auth_source?: string }).auth_source !== 'sso' && (
                    <button
                      onClick={() => handleResetPassword(u)}
                      style={subtleActionBtn}
                      title="Generate a password reset link for this user"
                    >
                      <Key size={12} aria-hidden="true" /> Reset password
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Pending invitations */}
          {(data?.pending_invitations?.length ?? 0) > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-mute)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                Pending invitations
              </div>
              {data!.pending_invitations.map(p => (
                <div key={p.invite_id} style={inviteRowStyle}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="email-text" style={{ fontSize: 13 }}>
                      {p.email}
                      {p.display_name && <span style={{ color: 'var(--color-text-soft)' }}> · {p.display_name}</span>}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-mute)' }}>
                      Expires {new Date(p.expires_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {showInvite && (
        <InviteUserDialog
          pharmacyId={pharmacyId}
          onClose={() => setShowInvite(false)}
          onInvited={(r) => {
            setRevealedInvite({ link: r.invite_link, email: r.email, role: r.role_name })
            setShowInvite(false)
            reload()
          }}
        />
      )}
    </Section>
  )
}

function InviteUserDialog({ pharmacyId, onClose, onInvited }: {
  pharmacyId: string
  onClose: () => void
  onInvited: (r: { invite_link: string; email: string; role_name: string }) => void
}) {
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [roleName, setRoleName] = useState('Manager')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null); setSubmitting(true)
    try {
      const r = await api.invitePharmacyUser(pharmacyId, email.trim(), roleName, displayName.trim() || undefined)
      onInvited(r)
    } catch (e) {
      setError(friendlyError(e))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div role="dialog" aria-modal="true" style={dialogBackdropP}>
      <div style={dialogStyleP}>
        <div style={dialogHeaderStyleP}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
            <UserPlus size={16} aria-hidden="true" style={{ verticalAlign: '-3px', marginRight: 6 }} />
            Invite user
          </h3>
          <button onClick={onClose} style={iconBtnStyleP} aria-label="Close"><X size={14} /></button>
        </div>
        <form onSubmit={submit} style={{ padding: 16 }}>
          <label style={editLabelStyleP} htmlFor="iu-email">Email</label>
          <input
            id="iu-email" type="email" required autoFocus
            value={email} onChange={e => setEmail(e.target.value)}
            disabled={submitting} style={editInputStyleP}
          />
          <label style={editLabelStyleP} htmlFor="iu-name">Display name (optional)</label>
          <input
            id="iu-name" type="text"
            value={displayName} onChange={e => setDisplayName(e.target.value)}
            disabled={submitting} style={editInputStyleP}
          />
          <label style={editLabelStyleP} htmlFor="iu-role">Role</label>
          <select
            id="iu-role" value={roleName} onChange={e => setRoleName(e.target.value)}
            disabled={submitting} style={editInputStyleP}
          >
            {ASSIGNABLE_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          {error && <div style={cardErrorBoxLocal}><AlertCircle size={13} /> {error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" onClick={onClose} disabled={submitting} style={subtleActionBtn}>Cancel</button>
            <button type="submit" disabled={submitting || !email.trim()} style={primaryBtnStyleP}>
              {submitting && <Loader2 size={13} className="spin" />}
              Send invitation
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function RevealCard({ title, link, note, onDismiss }: {
  title: string; link: string; note: string; onDismiss: () => void
}) {
  const fullUrl = `${window.location.origin}${link}`
  function copy() {
    navigator.clipboard.writeText(fullUrl)
    alert('Copied to clipboard.')
  }
  return (
    <div style={revealCardStyleP}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{title}</div>
          <code style={revealLinkStyleP}>{fullUrl}</code>
          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--color-text-soft)' }}>{note}</div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={copy} style={subtleActionBtn}>
            <Copy size={12} aria-hidden="true" /> Copy
          </button>
          <button onClick={onDismiss} style={iconBtnStyleP} aria-label="Dismiss"><X size={14} /></button>
        </div>
      </div>
    </div>
  )
}

const inviteBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '6px 10px', fontSize: 12, fontWeight: 500,
  background: 'var(--color-primary, #0d9488)', color: '#fff',
  border: 'none', borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit',
}
const userRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '8px 0',
  borderBottom: '1px solid var(--color-border)',
}
const inviteRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '6px 0',
  borderBottom: '1px dashed var(--color-border)',
}
const subtleActionBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '4px 10px', fontSize: 11, fontWeight: 500,
  background: 'transparent', color: 'var(--color-text-soft)',
  border: '1px solid var(--color-border)', borderRadius: 4,
  cursor: 'pointer', fontFamily: 'inherit',
}
const primaryBtnStyleP: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', fontSize: 13, fontWeight: 500,
  background: 'var(--color-primary, #0d9488)', color: '#fff',
  border: 'none', borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit',
}
const dialogBackdropP: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 1000,
  background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
}
const dialogStyleP: React.CSSProperties = {
  background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border)',
  borderRadius: 8, width: 'min(440px, 96vw)',
  boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
}
const dialogHeaderStyleP: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '12px 16px', borderBottom: '1px solid var(--color-border)',
}
const iconBtnStyleP: React.CSSProperties = {
  background: 'transparent', border: 'none', cursor: 'pointer', padding: 4,
  color: 'var(--color-text-soft)',
}
const editLabelStyleP: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 500,
  marginTop: 12, marginBottom: 4, color: 'var(--color-text-soft)',
}
const editInputStyleP: React.CSSProperties = {
  width: '100%', fontSize: 14, padding: '7px 10px',
  border: '1px solid var(--color-border)', borderRadius: 4,
  background: 'var(--color-surface, #fff)', color: 'var(--color-text)',
  fontFamily: 'inherit', boxSizing: 'border-box',
}
const revealCardStyleP: React.CSSProperties = {
  background: '#ecfdf5', border: '1px solid #6ee7b7',
  borderRadius: 6, padding: 12, marginBottom: 12,
}
const revealLinkStyleP: React.CSSProperties = {
  display: 'block', fontFamily: 'monospace', fontSize: 11,
  background: '#fff', padding: '6px 8px', borderRadius: 3,
  border: '1px solid #d1fae5', color: '#065f46',
  wordBreak: 'break-all', maxWidth: '100%',
}
const syntheticBadgeStyle: React.CSSProperties = {
  marginLeft: 6, fontSize: 9, fontWeight: 600, letterSpacing: 0.4,
  textTransform: 'uppercase', padding: '1px 5px', borderRadius: 8,
  background: '#fef3c7', color: '#92400e',
}
function statusBadgeStyle(status: string): React.CSSProperties {
  const colors: Record<string, [string, string]> = {
    active:   ['#dcfce7', '#166534'],
    pending:  ['#fef3c7', '#92400e'],
    locked:   ['#fee2e2', '#991b1b'],
    disabled: ['#f3f4f6', '#6b7280'],
  }
  const [bg, fg] = colors[status] || ['#f3f4f6', '#6b7280']
  return {
    marginLeft: 6, fontSize: 9, fontWeight: 600, letterSpacing: 0.4,
    textTransform: 'uppercase', padding: '1px 5px', borderRadius: 8,
    background: bg, color: fg,
  }
}
